import { useEffect, useId, useRef, useState } from "react";
import { IMAGE_ACCEPT } from "@/lib/uploadAccept";
import { checkImageFile } from "@/lib/checkImageFile";
import { fileFromTransfer, pasteIsImageUpload, pasteShortcut, transferHasFiles, transferMayHoldImage } from "@/lib/imageTransfer";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import {
  defaultCurrencyFor,
  MATERIAL_SIZE_UNITS,
  PRODUCT_APPROVAL_STATUSES,
  type ProductDetailDto,
} from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SIZE_UNITS, WIRE_SIZE_FIELDS, serializeWireSize } from "@/domain/wireSize";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useBlocker } from "react-router-dom";
import { cn } from "@/lib/cn";
import { formatQty } from "@/domain/money";
import { useAuth } from "@/app/auth";
import { useCan, useCanSeeCustomerRef, useDataRegion } from "@/app/access";
import { useCreateProduct, useSettings, useSuppliers, useUpdateProduct } from "@/api/hooks";
import { getLocalizedError, uploadImage } from "@/lib/api";
import { translateStatus } from "@/domain/i18n";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { QuickCreatePartyModal } from "@/components/domain/QuickCreatePartyModal";

import { ProductUnitProfilePanel } from "./ProductUnitProfilePanel";
import { DEFAULT_WEIGHT_UNIT, FALLBACK_CURRENCIES, initialProductForm, duplicateProductForm, duplicateVariantNeedsIdentity, isProductFormDirty, productMaterialPayload, productNumericPayload, productUnitPayload, type ProductForm } from "./productForm";
import { useProductTypeZh } from "./useProductTypeZh";
import { PRODUCT_FORM_GROUPS, filledCount, type ProductFieldKey, type ProductFieldPlacement, type ProductFormGroup } from "./productFormLayout";
import { FieldGrid, FormGroup, GroupNav, Hint, MoreBlock, SPAN_CLASS } from "@/components/ui/FormLayout";

/*
 * Bố cục (19/09/2026): 5 nhóm theo người dùng, ô rộng theo độ dài dữ liệu trên lưới 12 cột, gợi ý nằm DƯỚI ô, trường ít
 * dùng gập vào mục "Thêm" (tự mở khi có dữ liệu). Thứ tự và chỗ của từng trường nằm ở productFormLayout.ts — biểu mẫu chỉ
 * vẽ theo bảng đó, và test của bảng bắt buộc không trường nào bị bỏ sót.
 */

type ImageTarget = "image" | "approval";

/**
 * Ô ảnh: xem trước, tải lên, xoá. Dùng cho ảnh sản phẩm và chứng từ phê duyệt.
 *
 * Cả khối là chỗ thả ảnh (kéo từ WeChat, Finder…); bấm vào khung ảnh mở hộp chọn tệp như nút. Dán ảnh nằm ở form
 * (`data-image-slot` cho biết lần dán rơi vào ô nào) — Roary 09/10/2026.
 */
function ImageSlot({ target, url, uploading, disabled, size, uploadLabel, uploadingLabel, removeLabel, dropHint, dropTitle, error, onFile, onNoFile, onRemove }: {
  target: ImageTarget;
  url: string;
  uploading: boolean;
  disabled: boolean;
  size: "lg" | "md";
  uploadLabel: string;
  uploadingLabel: string;
  removeLabel: string;
  /** Chữ ngắn trong khung trống ("Kéo thả hoặc dán ảnh") — chỉ khung lớn đủ chỗ. */
  dropHint: string;
  /** Câu đầy đủ cho tooltip và trình đọc màn hình. */
  dropTitle: string;
  /**
   * Lỗi của RIÊNG ô này, hiện ngay dưới ô — khung lỗi chung nằm cuối form, người vừa thả ảnh không thấy. Ô lớn nằm trong cột
   * hẹp (chữ vỡ thành bảy dòng) nên form hiện lỗi của nó thành dải ngang dưới cả nhóm, và truyền `null` ở đây.
   */
  error: string | null;
  onFile: (file: File) => void;
  /** Thả vào mà không có tệp nào (ứng dụng chỉ đưa đường link). */
  onNoFile: () => void;
  onRemove: (() => void) | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  // dragenter / dragleave bắn cả khi lướt qua phần tử con — đếm để khung không nhấp nháy.
  const depth = useRef(0);
  const accepts = (e: React.DragEvent) => !disabled && transferMayHoldImage(e.dataTransfer);
  return (
    // Ảnh lớn (ảnh sản phẩm, cạnh các ô mã): nút xếp DƯỚI ảnh cho khối ảnh hẹp, chừa chỗ cho ba ô một hàng bên cạnh.
    <div
      data-image-slot={target}
      className={cn("flex gap-3", size === "lg" ? "flex-col items-start" : "items-center")}
      onDragEnter={(e) => { if (!accepts(e)) return; e.preventDefault(); depth.current += 1; setOver(true); }}
      onDragOver={(e) => { if (!accepts(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
      onDragLeave={() => { depth.current = Math.max(0, depth.current - 1); if (depth.current === 0) setOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        if (disabled) return;
        const file = fileFromTransfer(e.dataTransfer);
        if (file) onFile(file);
        else onNoFile();
      }}
    >
      <button
        type="button"
        disabled={disabled}
        title={dropTitle}
        aria-label={dropTitle}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "grid shrink-0 cursor-pointer place-items-center overflow-hidden rounded-sm border bg-sunken/40 hover:border-brand focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed",
          size === "lg" ? "size-24" : "size-16",
          url ? "border-line" : "border-dashed border-line-strong",
          over && "border-solid border-brand bg-brand-tint ring-2 ring-brand",
        )}
      >
        {url ? (
          <img src={url} alt="" className={"size-full object-cover" + (uploading ? " opacity-60" : "")} />
        ) : (
          <span className="flex flex-col items-center gap-1 px-1">
            <ImageIcon className={cn("size-5", over ? "text-brand" : "text-ink-faint")} />
            {size === "lg" && <span className="text-center text-2xs leading-tight text-ink-faint">{dropHint}</span>}
          </span>
        )}
      </button>
      <div className="flex flex-col gap-1">
        <label className="relative inline-flex rounded-sm focus-within:ring-2 focus-within:ring-brand">
          <span className="cursor-pointer rounded-sm border border-line-strong bg-surface px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-sunken">
            {uploading ? uploadingLabel : uploadLabel}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className="sr-only"
            disabled={disabled}
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              // Xoá ngay để chọn lại đúng tệp đó vẫn bắn onChange; File đã cầm trong tay vẫn đọc được.
              e.currentTarget.value = "";
              if (file) onFile(file);
            }}
          />
        </label>
        {onRemove && (
          <button type="button" disabled={disabled} className="text-left text-2xs text-danger hover:underline disabled:opacity-50" onClick={onRemove}>
            {removeLabel}
          </button>
        )}
        {error && <p role="alert" className="max-w-56 text-2xs text-danger">{error}</p>}
      </div>
    </div>
  );
}

/**
 * Supplier is a real FK (products.supplier_id); the name column is only a
 * snapshot the server writes from the chosen supplier. Free text here would
 * create supplier names that belong to no supplier — today zero rows do.
 * Mounted only for roles with supplier_cost:view, so the list is never fetched
 * by a role that may not read suppliers.
 */
function SupplierSelect({
  value,
  legacyName,
  onChange,
}: {
  value: string;
  legacyName: string | null;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useSuppliers({});
  const suppliers = data?.data ?? [];
  const [creating, setCreating] = useState(false);
  const known = suppliers.some((s) => s.id === value);
  return (
    <div>
      <FieldLabel hint={!value && legacyName ? legacyName : undefined}>
        {t("products.supplier")}
      </FieldLabel>
      {/* Ô CÓ TÌM KIẾM, kèm địa chỉ/điện thoại ở dòng phụ. Danh sách phẳng thì
          phải cuộn tay, và hai nhà cung cấp trùng tên trông y hệt nhau. */}
      <SearchableFilter
        className="w-full"
        label={t("products.supplier")}
        allLabel={t("products.selectSupplier")}
        searchPlaceholder={t("products.selectSupplier")}
        value={value}
        isLoading={isLoading}
        error={error ? t("suppliers.errorLoad") : null}
        onRetry={() => void refetch()}
        options={[
          // Nhà cung cấp đã bị xoá hoặc chưa tải về vẫn phải hiện, nếu không
          // mở một sản phẩm cũ ra là ô này tự rỗng và lưu lại thì mất liên kết.
          ...(value && !known ? [{ value, label: legacyName ?? value, sublabel: null }] : []),
          ...suppliers.map((sp) => ({
            value: sp.id,
            label: sp.companyName,
            sublabel: [sp.address, sp.phone].filter(Boolean).join(" · ") || null,
          })),
        ]}
        onChange={onChange}
        footer={(close) => (
          <button
            type="button"
            onClick={() => { close(); setCreating(true); }}
            className="w-full rounded-sm px-2.5 py-1.5 text-left text-xs text-brand hover:bg-brand-tint/40"
          >
            + {t("suppliers.newSupplier")}
          </button>
        )}
      />
      {/* Lối tắt tạo NCC ngay trong ô chọn: không có nó thì người dùng phải rời
          biểu mẫu đang dở (mất những gì đã gõ) để sang màn Nhà cung cấp. */}
      {creating && (
        <QuickCreatePartyModal
          kind="supplier"
          onClose={() => setCreating(false)}
          onCreated={onChange}
        />
      )}
    </div>
  );
}

export function ProductFormPanel({
  product: incomingProduct,
  duplicateFrom: incomingDuplicate,
  onClose,
  onSaved,
  onAddNext,
  variant = "modal",
}: {
  product?: ProductDetailDto;
  duplicateFrom?: ProductDetailDto;
  onSaved?: (id: string) => void;
  onAddNext?: () => void;
  onClose: () => void;
  /** Page editor is the primary entry; modal remains for compatibility. */
  variant?: "modal" | "page";
}) {
  const { t } = useTranslation();
  const product = useRef(incomingProduct).current;
  const [duplicateFrom, setDuplicateFrom] = useState(incomingDuplicate);
  const { user } = useAuth();
  const can = useCan();
  const canSeeCost = user ? can("supplier_cost:view") : false;
  const canSeeRef = useCanSeeCustomerRef();
  const canUpdate = user ? can("records:update") : false;
  const canCreate = user ? can("records:create") : false;
  const editing = !!product;
  const masterLocked = editing ? !canUpdate : !canCreate;
  const { data: settings } = useSettings();
  const typeZh = useProductTypeZh();

  const [f, setF] = useState(() => duplicateFrom ? duplicateProductForm(duplicateFrom, "USD") : initialProductForm(product, "USD"));
  const initialFormRef = useRef(f);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<"image" | "approval" | null>(null);
  const [imageError, setImageError] = useState<{ target: ImageTarget; message: string } | null>(null);
  /**
   * Ảnh tạm hiện NGAY khi chọn tệp, trước khi tải xong.
   *
   * Trên mạng chậm ở xưởng, khoảng chờ giữa lúc bấm và lúc ảnh hiện ra đủ dài
   * để người dùng tưởng chọn hụt và bấm lại. blob: URL có sẵn tức thì.
   */
  /**
   * Trường mang giá trị ÂM từ dữ liệu cũ.
   *
   * Form từ chối số âm (đúng: giá và số lượng âm là sai thật), nhưng khi mở một
   * sản phẩm di trú đã mang sẵn giá trị âm thì người dùng bấm Lưu và chỉ nhận
   * được "số không hợp lệ" mà không biết ô nào — nên bản ghi đó thành không sửa
   * được. Trên production còn đúng 1 sản phẩm như vậy (RT019MAN125, giá bán
   * −0,0001). Nói thẳng ô nào sai thay vì tự ý sửa dữ liệu của khách.
   */
  const legacyNegativeFields = product
    ? ([
        ["sellingPrice", product.sellingPrice],
        ["lowestPrice", product.lowestPrice],
        ["costPrice", product.costUnitPrice],
        ["upCharge", product.upCharge],
        ["moq", product.moq],
      ] as const)
        .filter(([, v]) => typeof v === "number" && v < 0)
        .map(([k]) => k)
    : [];

  const [pending, setPending] = useState<{ target: "image" | "approval"; url: string } | null>(null);
  useEffect(() => () => { if (pending) URL.revokeObjectURL(pending.url); }, [pending]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const confirmationTitleId = useId();
  const confirmationDescriptionId = useId();
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  const create = useCreateProduct();
  const update = useUpdateProduct(product?.id ?? "new");
  const busy = create.isPending || update.isPending;
  const set = (patch: Partial<ProductForm>) => {
    setSaved(false);
    setF((p) => ({ ...p, ...patch }));
  };
  const dirty = isProductFormDirty(f, initialFormRef.current);
  const allowNavigation = useRef(false);
  const inFlight = busy || uploading !== null;
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    variant === "page" && !allowNavigation.current && (dirty || inFlight) &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));

  const optionLists = settings?.businessOperations.optionLists;
  const configuredTypes = optionLists?.productTypes ?? [];
  const configuredUnits = optionLists?.productUnits ?? [];
  const currencies = optionLists?.currencies ?? FALLBACK_CURRENCIES;
  const legacyType = !!f.type && !configuredTypes.includes(f.type);
  const legacyUnit = !!f.unit && !configuredUnits.includes(f.unit);
  const legacyCurrency = !!f.currency && !currencies.includes(f.currency);
  const weightUnits = optionLists?.weightUnits?.length ? optionLists.weightUnits : [DEFAULT_WEIGHT_UNIT];
  const weightUnitLabel = f.weightUnit;

  // Settings load after the first render; adopt the configured default currency
  // for NEW products only, and only while the field is still untouched.
  // Theo công ty của người lập (yêu cầu v1.8 mục 6.2): tài khoản chỉ Trung Quốc → RMB, chỉ Việt Nam → USD (Cài đặt → Tiền tệ theo
  // công ty); tài khoản thấy cả hai công ty dùng tiền tệ mặc định chung như trước.
  const creatorRegion = useDataRegion();
  const defaultCurrency = settings
    ? defaultCurrencyFor(creatorRegion, settings.system.defaultCurrencyByRegion, settings.system.defaultCurrency)
    : undefined;
  const currencyAdopted = useRef(false);
  useEffect(() => {
    if (editing || duplicateFrom || currencyAdopted.current || !defaultCurrency) return;
    currencyAdopted.current = true;
    const initial = initialFormRef.current.currency;
    if (defaultCurrency === initial) return;
    initialFormRef.current = { ...initialFormRef.current, currency: defaultCurrency };
    setF((p) => (p.currency === initial ? { ...p, currency: defaultCurrency } : p));
  }, [editing, duplicateFrom, defaultCurrency]);

  // Cùng cách với đơn vị sản phẩm: hệ cũ có "giá trị mặc định" cho đơn vị, bản
  // mới đóng cứng "gross" nên mọi sản phẩm bán theo cái đều phải sửa tay.
  const defaultUnit = settings?.system.defaultProductUnit;
  const unitAdopted = useRef(false);
  useEffect(() => {
    if (editing || duplicateFrom || unitAdopted.current || !defaultUnit) return;
    unitAdopted.current = true;
    const initial = initialFormRef.current.unit;
    if (defaultUnit === initial) return;
    initialFormRef.current = { ...initialFormRef.current, unit: defaultUnit };
    setF((p) => (p.unit === initial ? { ...p, unit: defaultUnit } : p));
  }, [editing, duplicateFrom, defaultUnit]);

  const requestClose = () => {
    if (inFlight) return;
    if (variant === "page") { onClose(); return; }
    if (dirty) { setConfirmClose(true); return; }
    onClose();
  };

  useEffect(() => {
    if (!dirty && !inFlight) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (allowNavigation.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty, inFlight]);

  useEffect(() => {
    if (variant !== "page") return;
    const focusFrame = window.requestAnimationFrame(() => codeRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [variant]);

  useEffect(() => {
    if (!confirmClose || variant === "page") return;
    const focusFrame = window.requestAnimationFrame(() => keepEditingRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [confirmClose, variant]);

  /** Một đường tải ảnh cho cả ba cách đưa ảnh vào: chọn tệp, thả, dán. */
  const uploadTo = async (target: ImageTarget, file: File) => {
    if (inFlight) return;
    setUploading(target);
    setError(null);
    setImageError(null);
    let previewUrl: string | null = null;
    try {
      const checked = await checkImageFile(file);
      if (!checked.ok) {
        setImageError({ target, message: t(checked.reason === "size" ? "products.imageTooLarge" : "products.imageBadType") });
        return;
      }
      previewUrl = URL.createObjectURL(checked.file);
      setPending({ target, url: previewUrl });
      const r = await uploadImage(checked.file);
      set(
        target === "image"
          ? { imageUrl: r.url, imageKey: r.key }
          : { approvalImageUrl: r.url, approvalImageKey: r.key },
      );
    } catch (err) {
      setImageError({ target, message: getLocalizedError(err, t, "products.errorUpload") });
    } finally {
      setUploading(null);
      // Gỡ ảnh tạm sau khi đã có URL thật (hoặc sau khi lỗi): giữ lại là hiển
      // thị một ảnh không tồn tại trên máy chủ.
      setPending((prev) => {
        if (previewUrl && prev?.url === previewUrl) URL.revokeObjectURL(previewUrl);
        return prev?.url === previewUrl ? null : prev;
      });
    }
  };
  const shortcut = pasteShortcut(navigator.platform);
  const noImageMessage = (target: ImageTarget) => setImageError({ target, message: t("products.imageDropNoFile", { shortcut }) });

  /*
   * Dán ảnh (⌘V / Ctrl+V) ở bất cứ đâu trên form, không bắt người dùng tìm đúng ô để bấm trước: copy ảnh trong WeChat rồi dán
   * là xong. Lắng ở document vì con trỏ thường đang ở nền trang chứ không ở trong form. Rơi vào ô chứng từ phê duyệt thì đổi
   * ô đó, còn lại là ảnh sản phẩm. Đang mở hộp thoại khác (tạo nhanh nhà cung cấp, xác nhận đóng) thì không đụng tới.
   */
  const formRef = useRef<HTMLFormElement>(null);
  const uploadRef = useRef(uploadTo);
  uploadRef.current = uploadTo;
  const noImageRef = useRef(noImageMessage);
  noImageRef.current = noImageMessage;
  useEffect(() => {
    if (masterLocked) return;
    const onPaste = (e: ClipboardEvent) => {
      const form = formRef.current;
      if (!form || form.inert) return;
      if (Array.from(document.querySelectorAll('[aria-modal="true"]')).some((d) => !d.contains(form))) return;
      const el = e.target instanceof Element ? e.target : null;
      const slot = el?.closest("[data-image-slot]")?.getAttribute("data-image-slot");
      const textEntry = !!el?.closest("input, textarea, [contenteditable='true']");
      if (!pasteIsImageUpload(e.clipboardData, textEntry)) {
        // Đã chọn đúng ô ảnh mà clipboard không có ảnh: nói cách làm, đừng im lặng.
        if (slot && !fileFromTransfer(e.clipboardData)) noImageRef.current(slot === "approval" ? "approval" : "image");
        return;
      }
      const file = fileFromTransfer(e.clipboardData);
      if (!file) return;
      e.preventDefault();
      void uploadRef.current(slot === "approval" ? "approval" : "image", file);
    };
    // Thả hụt ra ngoài ô ảnh thì trình duyệt MỞ tệp thay cho trang và mất form đang nhập: chặn, và hiện con trỏ "không được"
    // ngoài ô. Ô ảnh tự xử lý trước (React gắn ở gốc ứng dụng, bên dưới document) nên ở đây chỉ còn lần kéo chưa ai nhận.
    const guardDrag = (e: DragEvent) => {
      if (e.defaultPrevented || !transferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
    };
    document.addEventListener("paste", onPaste);
    document.addEventListener("dragover", guardDrag);
    document.addEventListener("drop", guardDrag);
    return () => {
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("dragover", guardDrag);
      document.removeEventListener("drop", guardDrag);
    };
  }, [masterLocked]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlight || masterLocked) return;
    setError(null);
    setSaved(false);

    if (duplicateFrom && duplicateVariantNeedsIdentity(f, duplicateFrom)) {
      setError(t("productEditor.changeVariant"));
      codeRef.current?.focus();
      return;
    }
    if (editing && !f.productCode.trim()) {
      setError(t("productEditor.codeRequired"));
      codeRef.current?.focus();
      return;
    }

    const numeric = productNumericPayload(f, initialFormRef.current, editing, canSeeCost);
    if (numeric.invalid.length) {
      const fieldLabels = numeric.invalid.map((key) => t(
        key === "costUnitPrice" ? "products.costPrice" :
        ["dtdPrice", "exwPrice", "underMoqPrice"].includes(key) ? `productEditor.${key}` : `products.${key}`));
      setError(`${t("products.invalidNumber")}: ${fieldLabels.join(", ")}`);
      return;
    }

    const payload = {
      productCode: f.productCode.trim() || undefined,
      // Gửi thẳng chuỗi rỗng, KHÔNG phải `|| undefined`: `undefined` bị loại khỏi
      // JSON nên máy chủ không hề biết người dùng vừa xoá ô. Trước đây điều đó
      // khiến ô đã nhập là không xoá được, và nút "Remove image" bấm xong lưu
      // lại vẫn còn ảnh. Máy chủ đổi chuỗi rỗng thành NULL.
      description: f.description,
      descriptionZh: f.descriptionZh,
      subRef: f.subRef,
      customerRef: f.customerRef,
      type: f.type,
      brandName: f.brandName,
      color: f.color,
      colorZh: f.colorZh,
      size: f.size,
      ...productUnitPayload(f, initialFormRef.current, editing),
      ...productMaterialPayload(f),
      ...numeric.data,
      approvalStatus: f.approvalStatus as (typeof PRODUCT_APPROVAL_STATUSES)[number],
      currency: f.currency || "USD",
      // Lead times are TEXT columns holding numeric strings; keep them as typed so
      // a legacy row such as "-2" stays editable instead of failing validation.
      sampleLeadTime: f.sampleLeadTime.trim(),
      bulkLeadTime: f.bulkLeadTime.trim(),
      storage: f.storage,
      moldRef: f.moldRef,
      moldLocation: f.moldLocation,
      location: f.location,
      remarks: f.remarks,
      imageUrl: f.imageUrl,
      imageKey: f.imageKey,
      wireSize: serializeWireSize(f.wireSize),
      approvalImageUrl: f.approvalImageUrl,
      approvalImageKey: f.approvalImageKey,
      // Cost/supplier fields only submitted by roles allowed to see/set them.
      ...(canSeeCost
        ? {
            supplierProductRef: f.supplierProductRef,
            // Only sent when actually changed, so saving an untouched legacy row
            // (name, no FK) can never clear its supplier snapshot.
            ...(!editing && duplicateFrom?.supplierName && !duplicateFrom.supplierId && !f.supplierId
              ? { supplierName: duplicateFrom.supplierName }
              : !editing || f.supplierId !== (product?.supplierId ?? "")
                ? { supplierId: f.supplierId }
                : {}),
          }
        : {}),
    };
    try {
      const result = editing ? await update.mutateAsync(payload) : await create.mutateAsync(payload);
      if (!editing) {
        if (keepOpen) {
          // Bulk entry: keep the panel open, carry over what the next product in
          // the batch shares, and leave the code blank so the server auto-numbers.
          const next = {
            ...initialProductForm(undefined, f.currency),
            type: f.type,
            unit: f.unit,
            brandName: f.brandName,
            supplierId: f.supplierId,
          };
          currencyAdopted.current = true;
          unitAdopted.current = true;
          setDuplicateFrom(undefined);
          setF(next);
          initialFormRef.current = next;
          setSaved(true);
          onAddNext?.();
          window.requestAnimationFrame(() => codeRef.current?.focus());
          return;
        }
      }
      allowNavigation.current = true;
      if (onSaved) onSaved(result.data.id);
      else onClose();
    } catch (err) {
      setError(getLocalizedError(err, t, "products.errorSave"));
    }
  };

  const numberInput = (id: string, key: "moq" | "mcq" | "msq" | "reorderPoint" | "sampleLeadTime" | "bulkLeadTime", label: string, hint?: string) => (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} inputMode="numeric" className="font-mono tabular" value={f[key]} onChange={(e) => set({ [key]: e.target.value })}
        aria-describedby={hint ? `${id}-hint` : undefined} />
      {hint ? <Hint id={`${id}-hint`}>{hint}</Hint> : null}
    </div>
  );
  /** Ô tiền: đơn vị tiền nằm trong ô (hậu tố), không đặt ở mép phải nhãn. */
  const moneyInput = (id: string, key: "sellingPrice" | "lowestPrice" | "upCharge" | "dtdPrice" | "exwPrice" | "underMoqPrice" | "costUnitPrice", label: string, hint?: string) => (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} inputMode="decimal" className="font-mono tabular" trailing={f.currency} value={f[key]} onChange={(e) => set({ [key]: e.target.value })}
        aria-describedby={hint ? `${id}-hint` : undefined} />
      {hint ? <Hint id={`${id}-hint`}>{hint}</Hint> : null}
    </div>
  );
  const textInput = (id: string, key: "subRef" | "customerRef" | "brandName" | "color" | "colorZh" | "size" | "supplierProductRef" | "storage" | "location" | "moldRef" | "moldLocation" | "materialType" | "materialHardness",
    label: string, opts: { hint?: string; mono?: boolean; maxLength?: number } = {}) => (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} maxLength={opts.maxLength} className={opts.mono ? "font-mono tabular" : undefined} value={f[key]} onChange={(e) => set({ [key]: e.target.value })}
        aria-describedby={opts.hint ? `${id}-hint` : undefined} />
      {opts.hint ? <Hint id={`${id}-hint`}>{opts.hint}</Hint> : null}
    </div>
  );

  /** Số + đơn vị (mm / cm / inch) cho chiều rộng và độ dày vật liệu — cùng kiểu ô khối lượng / kích thước dây. */
  const materialSizeInput = (id: string, key: "materialWidth" | "materialThickness", unitKey: "materialWidthUnit" | "materialThicknessUnit", label: string) => (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex gap-1.5">
        <Input id={id} inputMode="decimal" className="min-w-0 flex-1 font-mono tabular" value={f[key]} onChange={(e) => set({ [key]: e.target.value })} />
        <Select containerClassName="w-24 shrink-0" aria-label={`${label} — ${t("common.unit")}`} value={f[unitKey]} onChange={(e) => set({ [unitKey]: e.target.value })}>
          {/* Đơn vị lạ (nhập ngoài biểu mẫu) vẫn hiện để không bị đổi lặng lẽ. */}
          {!(MATERIAL_SIZE_UNITS as readonly string[]).includes(f[unitKey]) && <option value={f[unitKey]}>{f[unitKey]}</option>}
          {MATERIAL_SIZE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </Select>
      </div>
    </div>
  );

  /** Phần chung của hai ô ảnh. Khoá cả khi không có quyền sửa hay đang lưu: fieldset chỉ khoá nút và ô chọn tệp, không khoá thả. */
  const slotProps = (target: ImageTarget) => ({
    target,
    uploading: uploading === target,
    disabled: uploading !== null || masterLocked || busy,
    uploadingLabel: t("common.uploading"),
    dropHint: t("products.imageDropHint"),
    dropTitle: t("products.imageDropTitle", { shortcut }),
    error: imageError?.target === target ? imageError.message : null,
    onFile: (file: File) => void uploadTo(target, file),
    onNoFile: () => noImageMessage(target),
  });
  /**
   * Ô nhập của TỪNG trường. `Record` bắt buộc mỗi ô trong bảng bố cục có đúng một ô nhập ở đây — thiếu là lỗi biên dịch.
   * Logic giữ nguyên bản trước 19/09/2026 (giá trị cũ ngoài danh sách vẫn chọn được, ảnh tạm khi đang tải, v.v.).
   */
  const fields: Record<ProductFieldKey, () => React.ReactNode> = {
    image: () => (
      <div>
        <FieldLabel>{t("products.image")}</FieldLabel>
        {/* Ảnh tạm thắng trong lúc đang tải, để ô không trống. */}
        <ImageSlot {...slotProps("image")} error={null} size="lg" url={pending?.target === "image" ? pending.url : f.imageUrl}
          uploadLabel={t("products.uploadImage")} removeLabel={t("products.removeImage")}
          onRemove={f.imageUrl ? () => set({ imageUrl: "", imageKey: "" }) : null} />
      </div>
    ),
    productCode: () => (
      <div>
        <FieldLabel htmlFor="p-code">{t("products.code")}</FieldLabel>
        <Input id="p-code" required={editing} maxLength={60} className="font-mono tabular" ref={codeRef} data-autofocus="true"
          value={f.productCode} onChange={(e) => set({ productCode: e.target.value })} aria-describedby={editing ? undefined : "p-code-hint"} />
        {editing ? null : <Hint id="p-code-hint">{t("products.codeHint")}</Hint>}
      </div>
    ),
    subRef: () => textInput("p-subref", "subRef", t("products.subRef"), { hint: t("products.subRefHint"), mono: true, maxLength: 120 }),
    // Người không được thấy mã hàng của khách thì không có ô này; máy chủ cũng bỏ qua giá trị họ gửi (YC01-L1).
    customerRef: () => (canSeeRef ? textInput("p-custref", "customerRef", t("products.customerRef"), { mono: true, maxLength: 120 }) : null),
    type: () => (
      <div>
        <FieldLabel htmlFor="p-type">{t("products.type")}</FieldLabel>
        <Select id="p-type" containerClassName="w-full" value={f.type} onChange={(e) => set({ type: e.target.value })}>
          <option value="">{t("products.selectType")}</option>
          {legacyType ? <option value={f.type}>{f.type} — {t("products.legacyType")}</option> : null}
          {/* Tên tiếng Hoa (Cài đặt) nằm dưới tên tiếng Anh, như mọi màn sản phẩm; giá trị lưu vẫn là chữ tiếng Anh.
              Ghép chung một dòng thì ô hẹp cắt mất phần tiếng Hoa (Roary 19/10/2026). */}
          {configuredTypes.map((type) => <option key={type} value={type} data-sublabel={typeZh(type) || undefined}>{type}</option>)}
        </Select>
        {f.type && typeZh(f.type) ? <Hint>{typeZh(f.type)}</Hint> : null}
      </div>
    ),
    brandName: () => textInput("p-brand", "brandName", t("products.brand")),
    unit: () => (
      <div>
        <FieldLabel htmlFor="p-unit">{t("products.unit")}</FieldLabel>
        <Select id="p-unit" containerClassName="w-full" value={f.unit} onChange={(e) => set({ unit: e.target.value })}>
          <option value="">{t("products.selectUnit")}</option>
          {legacyUnit ? <option value={f.unit}>{f.unit} — {t("products.legacyUnit")}</option> : null}
          {configuredUnits.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
        </Select>
      </div>
    ),
    description: () => (
      <div>
        <FieldLabel htmlFor="p-desc-en">{t("products.descriptionEn")}</FieldLabel>
        <Input id="p-desc-en" value={f.description} onChange={(e) => set({ description: e.target.value })} />
      </div>
    ),
    color: () => textInput("p-color", "color", t("products.color"), { maxLength: 120 }),
    colorZh: () => textInput("p-color-zh", "colorZh", t("products.colorZh"), { maxLength: 120 }),
    size: () => textInput("p-size", "size", t("products.size")),
    weightPer100: () => (
      <div>
        <FieldLabel htmlFor="p-weight">{t("products.weightPer100")}</FieldLabel>
        {/* Đơn vị CHỌN được, không đóng cứng "g": sản phẩm tính bằng kilogram trước đây không nhập đúng được. Danh sách do
            quản trị quản lý ở Cài đặt. */}
        <div className="flex gap-1.5">
          <Input id="p-weight" inputMode="decimal" className="min-w-0 flex-1 font-mono tabular" value={f.weightPer100} onChange={(e) => set({ weightPer100: e.target.value })} />
          <Select containerClassName="w-24 shrink-0" aria-label={`${t("products.weightPer100")} — ${t("common.unit")}`} value={weightUnitLabel}
            onChange={(e) => set({ weightUnit: e.target.value })}>
            <option value="">{t("products.selectUnit")}</option>
            {weightUnits.map((u) => <option key={u} value={u}>{u}</option>)}
            {/* Giá trị cũ không còn trong danh sách vẫn phải chọn được, nếu không mở một sản phẩm cũ ra là đơn vị bị đổi lặng lẽ. */}
            {weightUnitLabel && !weightUnits.includes(weightUnitLabel) && <option value={weightUnitLabel}>{weightUnitLabel}</option>}
          </Select>
        </div>
      </div>
    ),
    descriptionZh: () => (
      <div>
        <FieldLabel htmlFor="p-desc-zh">{t("products.descriptionZh")}</FieldLabel>
        <Input id="p-desc-zh" value={f.descriptionZh} onChange={(e) => set({ descriptionZh: e.target.value })} />
      </div>
    ),
    wireSize: () => (
      <div className="grid grid-cols-1 gap-3 @md:grid-cols-3">
        {WIRE_SIZE_FIELDS.map((k) => (
          <div key={k}>
            <FieldLabel htmlFor={`p-ws-${k}`}>{t(`products.wire.${k}`)}</FieldLabel>
            <div className="flex gap-1.5">
              <Input id={`p-ws-${k}`} inputMode="decimal" className="min-w-0 flex-1 font-mono tabular" value={f.wireSize[k] ?? ""}
                onChange={(e) => set({ wireSize: { ...f.wireSize, [k]: e.target.value } })} />
              <Select containerClassName="w-24 shrink-0" aria-label={`${t(`products.wire.${k}`)} — ${t("common.unit")}`} value={f.wireSize[`${k}Unit`] || "mm"}
                onChange={(e) => set({ wireSize: { ...f.wireSize, [`${k}Unit`]: e.target.value } })}>
                {f.wireSize[`${k}Unit`] && !(SIZE_UNITS as readonly string[]).includes(f.wireSize[`${k}Unit`] ?? "") && (
                  <option value={f.wireSize[`${k}Unit`]}>{f.wireSize[`${k}Unit`]}</option>
                )}
                {SIZE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </Select>
            </div>
          </div>
        ))}
      </div>
    ),
    materialType: () => textInput("p-mat-type", "materialType", t("products.materialType"), { hint: t("products.materialTypeHint"), maxLength: 120 }),
    materialWidth: () => materialSizeInput("p-mat-width", "materialWidth", "materialWidthUnit", t("products.materialWidth")),
    materialThickness: () => materialSizeInput("p-mat-thick", "materialThickness", "materialThicknessUnit", t("products.materialThickness")),
    materialHardness: () => textInput("p-mat-hard", "materialHardness", t("products.materialHardness"), { hint: t("products.materialHardnessHint"), maxLength: 120 }),
    currency: () => (
      <div>
        <FieldLabel htmlFor="p-cur">{t("products.currencyLabel")}</FieldLabel>
        <Select id="p-cur" containerClassName="w-full" value={f.currency} onChange={(e) => set({ currency: e.target.value })}>
          {legacyCurrency ? <option value={f.currency}>{f.currency}</option> : null}
          {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </div>
    ),
    sellingPrice: () => moneyInput("p-sell", "sellingPrice", t("products.sellingPrice"), t("productEditor.sellingPriceHint")),
    upCharge: () => moneyInput("p-upcharge", "upCharge", t("products.upCharge")),
    moq: () => numberInput("p-moq", "moq", t("products.moq"), t("products.moqHint")),
    sampleLeadTime: () => numberInput("p-slt", "sampleLeadTime", t("products.sampleLeadTime")),
    bulkLeadTime: () => numberInput("p-blt", "bulkLeadTime", t("products.bulkLeadTime")),
    lowestPrice: () => moneyInput("p-lowest", "lowestPrice", t("products.lowestPrice"), t("products.lowestPriceHint")),
    dtdPrice: () => moneyInput("p-dtdPrice", "dtdPrice", t("productEditor.dtdPrice")),
    exwPrice: () => moneyInput("p-exwPrice", "exwPrice", t("productEditor.exwPrice")),
    underMoqPrice: () => moneyInput("p-underMoqPrice", "underMoqPrice", t("productEditor.underMoqPrice")),
    mcq: () => numberInput("p-mcq", "mcq", t("products.mcq"), t("products.mcqHint")),
    msq: () => numberInput("p-msq", "msq", t("products.msq"), t("products.msqHint")),
    supplierId: () => (
      <SupplierSelect value={f.supplierId} legacyName={product?.supplierName ?? duplicateFrom?.supplierName ?? null} onChange={(supplierId) => set({ supplierId })} />
    ),
    supplierProductRef: () => textInput("p-supref", "supplierProductRef", t("products.supplierProductRef")),
    costUnitPrice: () => moneyInput("p-cost", "costUnitPrice", t("products.costPrice")),
    approvalStatus: () => (
      <div>
        <FieldLabel htmlFor="p-appr">{t("products.approval")}</FieldLabel>
        <Select id="p-appr" containerClassName="w-full" value={f.approvalStatus}
          onChange={(e) => set({ approvalStatus: e.target.value as (typeof PRODUCT_APPROVAL_STATUSES)[number] })}>
          {PRODUCT_APPROVAL_STATUSES.map((s) => <option key={s} value={s}>{translateStatus(t, s)}</option>)}
        </Select>
      </div>
    ),
    approvalImage: () => (
      <div>
        <FieldLabel>{t("products.approvalProof")}</FieldLabel>
        <ImageSlot {...slotProps("approval")} size="md" url={pending?.target === "approval" ? pending.url : f.approvalImageUrl}
          uploadLabel={t("products.uploadApprovalProof")} removeLabel={t("products.removeApprovalProof")}
          onRemove={f.approvalImageUrl ? () => set({ approvalImageUrl: "", approvalImageKey: "" }) : null} />
        <Hint>{t("products.approvalProofHint")}</Hint>
      </div>
    ),
    // Kho & LOT là nguồn tồn duy nhất (18/09/2026): không có ô gõ số tồn, kể cả khi tạo mới — chỉ hiện số và lối sang từng lô.
    stock: () => (
      <div>
        <span className="mb-1.5 block text-xs font-medium text-ink-soft">{t("products.stock")}</span>
        <p className="flex min-h-9 flex-wrap items-center gap-x-3 font-mono tabular text-sm text-ink">
          {editing && product ? `${formatQty(product.stockQuantity)} ${product.unit ?? ""}` : "—"}
          {editing && product ? (
            <Link to={`/products/${product.id}/stock`} className="font-sans text-xs text-brand hover:underline">{t("productEditor.stockLink")}</Link>
          ) : null}
        </p>
        <Hint>{t("productEditor.stockReadOnly")}</Hint>
      </div>
    ),
    // Ngưỡng đặt lại hàng — KHÔNG phải MOQ. Cảnh báo tồn thấp trên Dashboard đọc đúng ô này; để trống thì sản phẩm không được
    // cảnh báo, và Dashboard nói rõ còn bao nhiêu sản phẩm như vậy.
    reorderPoint: () => numberInput("p-reorder", "reorderPoint", t("products.reorderPoint"), t("products.reorderPointHint")),
    // Kệ mặc định (anh Toàn chọn 19/09/2026): ô "Vị trí" của hệ cũ, nay dùng để điền sẵn vị trí khi nhận hàng và khi cất /
    // chuyển lô. Vị trí THẬT của hàng nằm ở từng lô (Kho & LOT) — một mã có thể nằm nhiều kệ.
    location: () => textInput("p-loc", "location", t("productEditor.defaultBin"), { hint: t("productEditor.defaultBinHint"), mono: true, maxLength: 120 }),
    storage: () => textInput("p-storage", "storage", t("products.storage"), { hint: t("products.storageHint") }),
    // Khuôn nằm ở xưởng ép / nhà cung cấp, KHÁC chỗ để hàng.
    moldRef: () => textInput("p-mold-ref", "moldRef", t("products.moldRef")),
    moldLocation: () => textInput("p-mold-loc", "moldLocation", t("products.moldLocation")),
    remarks: () => (
      <div>
        <FieldLabel htmlFor="p-remarks">{t("common.remarks")}</FieldLabel>
        <textarea id="p-remarks" rows={2} value={f.remarks} onChange={(e) => set({ remarks: e.target.value })}
          className="w-full resize-y rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-[color:var(--color-brand)]/15" />
      </div>
    ),
  };

  const visibleGroups = PRODUCT_FORM_GROUPS.filter((g) => !g.costOnly || canSeeCost);
  const navGroups = [
    ...visibleGroups.map((g) => ({ id: `product-group-${g.id}`, title: t(`productEditor.groups.${g.id}`) })),
    ...(product?.id ? [{ id: "product-group-units", title: t("unitPricing.productPanelTitle") }] : []),
  ];
  const place = (placement: ProductFieldPlacement) => (
    <div key={placement.key} className={cn("min-w-0", SPAN_CLASS[placement.span])}>{fields[placement.key]()}</div>
  );
  const renderGroup = (g: ProductFormGroup) => (
    <FormGroup key={g.id} id={`product-group-${g.id}`} title={t(`productEditor.groups.${g.id}`)} hint={t(`productEditor.groups.${g.id}Hint`)}>
      {g.aside ? (
        <>
          <div className="flex flex-col gap-4 @md:flex-row @md:items-start">
            <div className="shrink-0">{fields[g.aside]()}</div>
            <div className="@container min-w-0 flex-1"><FieldGrid>{g.fields.map(place)}</FieldGrid></div>
          </div>
          {g.aside === "image" && imageError?.target === "image" && (
            <p role="alert" className="mt-3 rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{imageError.message}</p>
          )}
        </>
      ) : (
        <FieldGrid>{g.fields.map(place)}</FieldGrid>
      )}
      {(g.more ?? []).map((m) => (
        // Mở sẵn theo dữ liệu LÚC MỞ biểu mẫu (không theo từng phím gõ — xoá ô cuối cùng không làm mục gập lại giữa chừng).
        <MoreBlock key={m.id} title={t(`productEditor.more.${m.id}`)} defaultOpen={filledCount(initialFormRef.current, m.fields) > 0}
          filledLabel={filledCount(f, m.fields) > 0 ? t("productEditor.moreFilled", { count: filledCount(f, m.fields) }) : null}>
          <FieldGrid>{m.fields.map(place)}</FieldGrid>
        </MoreBlock>
      ))}
    </FormGroup>
  );

  const form = (
      <form
        ref={formRef}
        onSubmit={submit}
        className={variant === "page" ? undefined : "flex min-h-0 flex-1 flex-col"}
        inert={confirmClose || blocker.state === "blocked"}
        aria-hidden={confirmClose || blocker.state === "blocked"}
      >
        <div
          className={
            variant === "page"
              ? "space-y-4"
              : "min-h-0 flex-1 space-y-4 overflow-y-auto p-5"
          }
        >
          {duplicateFrom && <p className="rounded-sm border border-line bg-info-tint px-3 py-2 text-sm text-ink-soft">{t("productEditor.duplicateHint", { code: duplicateFrom.productCode })}</p>}
          {variant === "page" && <GroupNav label={t("productEditor.groupNav")} groups={navGroups} />}
          <fieldset disabled={masterLocked || busy} className="space-y-4 border-0 p-0">
          <div className={cn("grid items-start gap-4", variant === "page" && "2xl:grid-cols-2")}>
            {(["left", "right"] as const).map((column) => (
              <div key={column} className="min-w-0 space-y-4">
                {visibleGroups.filter((g) => g.column === column).map(renderGroup)}
              </div>
            ))}
          </div>

          {/* Chỉ có nghĩa với sản phẩm ĐÃ LƯU: quy đổi gắn theo id sản phẩm. */}
          {product?.id && (
            <FormGroup id="product-group-units" title={t("unitPricing.productPanelTitle")} hint={t("productEditor.groups.unitsHint")}>
              <ProductUnitProfilePanel productId={product.id} embedded />
            </FormGroup>
          )}

          {legacyNegativeFields.length > 0 && (
            <p className="rounded-sm border border-[color:var(--color-warn)]/30 bg-warn-tint px-3 py-2 text-xs text-ink-soft">
              {t("productEditor.legacyNegative", {
                fields: legacyNegativeFields.map((k) => t(`products.${k}`)).join(", "),
              })}
            </p>
          )}
          {error && (
            <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{error}</p>
          )}
          </fieldset>

        </div>
        <div
          className={cn(
            "flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-2 border-t border-line bg-surface px-5 py-3.5",
            // Trang: thanh nổi dính đáy vùng cuộn (biểu mẫu là các thẻ nhóm, không còn một khung bao ngoài).
            variant === "page" && "sticky bottom-0 z-10 mt-4 rounded-md border shadow-[var(--shadow-card)]",
          )}
        >
          {!editing && (
            <label className="mr-auto flex items-center gap-2 text-xs text-ink-soft">
              <input
                type="checkbox"
                disabled={inFlight}
                checked={keepOpen}
                onChange={(e) => setKeepOpen(e.target.checked)}
                className="size-3.5 accent-[color:var(--color-brand)]"
              />
              {t("products.keepOpenNextCode")}
            </label>
          )}
          {saved && <p className="text-xs text-ok">{t("common.saved")}</p>}
          <Button type="button" variant="secondary" size="sm" disabled={inFlight} onClick={requestClose}>
            {t("common.cancel")}
          </Button>
          {!masterLocked && (
            <Button type="submit" size="sm" disabled={inFlight}>
              {busy ? t("common.saving") : t("common.save")}
            </Button>
          )}
        </div>
      </form>
  );

  if (variant === "page") {
    return (
      <>
        {form}
        <ConfirmDialog
          open={blocker.state === "blocked"}
          busy={inFlight}
          tone="warn"
          title={t("common.unsavedTitle")}
          description={t("common.unsavedDescription")}
          confirmLabel={t("common.discardChanges")}
          cancelLabel={t("common.keepEditing")}
          onCancel={() => { if (blocker.state === "blocked") blocker.reset(); }}
          onConfirm={() => { if (!inFlight && blocker.state === "blocked") blocker.proceed(); }}
        />
      </>
    );
  }

  return (
    <Modal
      eyebrow={editing ? t("common.editMode") : t("common.createMode")}
      title={editing ? product.productCode : t("products.newProduct")}
      closeLabel={t("common.cancel")}
      onClose={requestClose}
    >
      {form}
      {confirmClose ? (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-ink/35 p-4 backdrop-blur-[2px]"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setConfirmClose(false);
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={confirmationTitleId}
            aria-describedby={confirmationDescriptionId}
            className="w-full max-w-md rounded-lg border border-line bg-surface shadow-[var(--shadow-pop)]"
          >
            <div className="flex gap-3 px-5 py-5">
              <span className="grid size-9 shrink-0 place-items-center rounded-sm bg-warn-tint text-warn">
                <TriangleAlert className="size-5" />
              </span>
              <div className="min-w-0">
                <h3 id={confirmationTitleId} className="text-lg font-semibold text-ink">
                  {t("common.unsavedTitle")}
                </h3>
                <p id={confirmationDescriptionId} className="mt-1.5 text-sm leading-relaxed text-ink-soft">
                  {t("common.unsavedDescription")}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-line bg-paper px-5 py-3.5">
              <Button
                ref={keepEditingRef}
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setConfirmClose(false)}
              >
                {t("common.keepEditing")}
              </Button>
              <Button type="button" variant="danger" size="sm" onClick={onClose}>
                {t("common.discardChanges")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
